import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Building2,
  CalendarRange,
  CheckCircle2,
  ChevronDown,
  Clock,
  FileText,
  Filter,
  Flag,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Search,
  Swords,
  TrendingUp,
  Wallet,
  XCircle,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/brand/PageLoader";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/Textarea";
import { supabase } from "@/lib/supabase";
import { formatMoneyMinor } from "@/lib/money";
import { env } from "@/lib/env";

type Status = "pending" | "approved" | "rejected" | "changes_requested";

interface Application {
  id: string;
  venue_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  address_line1: string;
  city: string;
  region: string;
  country: string;
  booking_system: string;
  notes: string | null;
  status: Status;
  created_at: string;
  reviewed_at: string | null;
  invited_at: string | null;
  rejection_reason: string | null;
  stripe_connect_status: string | null;
  vp_agreement_signed_at: string | null;
}

/**
 * Lazy-loaded snapshot of how the venue is doing post-approval.
 * Mirrors what the venue sees on their own dashboard (VenueOverview
 * checklist + VenueAnalytics tiles) so admins can track onboarding
 * progress + performance without context-switching into a "view as
 * venue" mode.
 *
 * Loaded per-venue on row expansion to keep the page fast even when
 * we have dozens of approved venues.
 */
interface VenueSnapshot {
  courtCount: number;
  slotCount: number;
  monthlyConfirmed: number;
  allTimeConfirmed: number;
  monthlyRevenueMinor: number;
  allTimeRevenueMinor: number;
  capturedSeats: number;
  totalTargetSeats: number;
  flagCount: number;
  currency: string;
}

const STATUS_META: Record<
  Status,
  { label: string; color: string; bg: string; icon: React.ReactNode }
> = {
  pending: {
    label: "Pending",
    color: "#FF6200",
    bg: "rgba(255,98,0,0.12)",
    icon: <Clock className="h-3 w-3" />,
  },
  approved: {
    label: "Approved",
    color: "#4ade80",
    bg: "rgba(74,222,128,0.12)",
    icon: <CheckCircle2 className="h-3 w-3" />,
  },
  rejected: {
    label: "Rejected",
    color: "#f87171",
    bg: "rgba(248,113,113,0.12)",
    icon: <XCircle className="h-3 w-3" />,
  },
  changes_requested: {
    label: "Changes requested",
    color: "#fbbf24",
    bg: "rgba(251,191,36,0.12)",
    icon: <Clock className="h-3 w-3" />,
  },
};

/**
 * Admin → Venues. Reviews inbound venue applications, approves them
 * (triggers the approve-venue Edge Function which creates the profile
 * + sends a magic-link invite), or rejects them with a reason.
 */
const VALID_FILTERS = new Set<Status | "all">([
  "pending",
  "approved",
  "rejected",
  "changes_requested",
  "all",
]);

export function AdminVenues() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilter = (() => {
    const s = searchParams.get("status");
    return s && VALID_FILTERS.has(s as Status | "all")
      ? (s as Status | "all")
      : "pending";
  })();

  const [apps, setApps] = useState<Application[] | null>(null);
  const [filter, setFilter] = useState<Status | "all">(initialFilter);
  const [query, setQuery] = useState("");

  // Keep the ?status= query param in sync with the filter tab, so
  // (a) back/forward navigation works and (b) deep-links from the
  // Overview stat cards pre-select the right tab.
  useEffect(() => {
    const current = searchParams.get("status");
    if (filter === "pending" && !current) return;
    if (current === filter) return;
    const next = new URLSearchParams(searchParams);
    if (filter === "pending") {
      next.delete("status");
    } else {
      next.set("status", filter);
    }
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectingId, setRejectingId] = useState<string | null>(null);

  async function load() {
    const { data, error } = await supabase
      .from("venue_applications")
      .select(
        "id, venue_name, contact_name, contact_email, contact_phone, address_line1, city, region, country, booking_system, notes, status, created_at, reviewed_at, invited_at, rejection_reason, stripe_connect_status, vp_agreement_signed_at"
      )
      .order("created_at", { ascending: false });
    if (error) {
      toast.error("Failed to load applications: " + error.message);
      setApps([]);
      return;
    }
    setApps((data ?? []) as Application[]);
  }

  // Cache of lazy-loaded venue snapshots keyed by venue_application.id.
  // null entry = currently loading; missing = not yet requested.
  const [snapshots, setSnapshots] = useState<
    Record<string, VenueSnapshot | null>
  >({});

  /**
   * Pulls the venue's onboarding-state + performance numbers in one
   * round-trip: court count, upcoming slot count, all matches +
   * participants for those courts, plus no-show flags. Computes the
   * same figures the venue sees on their own VenueAnalytics page.
   * Cached in `snapshots` so re-expanding a row is free.
   */
  async function loadSnapshot(venueAppId: string) {
    if (snapshots[venueAppId]) return;
    setSnapshots((s) => ({ ...s, [venueAppId]: null })); // mark loading

    const monthStart = new Date();
    monthStart.setDate(1);
    monthStart.setHours(0, 0, 0, 0);

    const { data: courtRows } = await supabase
      .from("courts")
      .select("id, currency")
      .eq("venue_application_id", venueAppId);
    const courts = (courtRows ?? []) as Array<{ id: string; currency: string }>;
    const courtIds = courts.map((c) => c.id);
    const currency = courts[0]?.currency ?? "GBP";

    if (courtIds.length === 0) {
      setSnapshots((s) => ({
        ...s,
        [venueAppId]: {
          courtCount: 0,
          slotCount: 0,
          monthlyConfirmed: 0,
          allTimeConfirmed: 0,
          monthlyRevenueMinor: 0,
          allTimeRevenueMinor: 0,
          capturedSeats: 0,
          totalTargetSeats: 0,
          flagCount: 0,
          currency,
        },
      }));
      return;
    }

    const [{ count: slotCount }, { data: matchRows }] = await Promise.all([
      supabase
        .from("availability_slots")
        .select("id", { count: "exact", head: true })
        .in("court_id", courtIds)
        .gte("starts_at", new Date().toISOString()),
      supabase
        .from("matches")
        .select(
          "id, status, target_players, confirmed_at, participants:match_participants ( status, unit_price_minor )",
        )
        .in("court_id", courtIds),
    ]);

    const matches = (matchRows ?? []) as Array<{
      id: string;
      status: string;
      target_players: number;
      confirmed_at: string | null;
      participants: { status: string; unit_price_minor: number }[];
    }>;

    let monthlyConfirmed = 0;
    let allTimeConfirmed = 0;
    let monthlyRevenueMinor = 0;
    let allTimeRevenueMinor = 0;
    let capturedSeats = 0;
    let totalTargetSeats = 0;
    for (const m of matches) {
      const isConfirmed =
        m.status === "confirmed" || m.status === "completed";
      if (!isConfirmed) continue;
      allTimeConfirmed++;
      totalTargetSeats += m.target_players;
      const captured = (m.participants ?? []).filter(
        (p) => p.status === "captured",
      );
      capturedSeats += captured.length;
      const revenue = captured.reduce(
        (sum, p) => sum + p.unit_price_minor,
        0,
      );
      allTimeRevenueMinor += revenue;
      if (m.confirmed_at && new Date(m.confirmed_at) >= monthStart) {
        monthlyConfirmed++;
        monthlyRevenueMinor += revenue;
      }
    }

    const { count: flagCount } = await supabase
      .from("no_show_flags")
      .select("id", { count: "exact", head: true })
      .in(
        "match_id",
        matches.map((m) => m.id),
      );

    setSnapshots((s) => ({
      ...s,
      [venueAppId]: {
        courtCount: courts.length,
        slotCount: slotCount ?? 0,
        monthlyConfirmed,
        allTimeConfirmed,
        monthlyRevenueMinor,
        allTimeRevenueMinor,
        capturedSeats,
        totalTargetSeats,
        flagCount: flagCount ?? 0,
        currency,
      },
    }));
  }

  // Trigger snapshot load when a row is expanded for an approved
  // venue. Pending/rejected venues don't have meaningful onboarding
  // or performance data so we skip the query.
  useEffect(() => {
    if (!expandedId || !apps) return;
    const app = apps.find((a) => a.id === expandedId);
    if (!app || app.status !== "approved") return;
    void loadSnapshot(expandedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedId, apps]);

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!apps) return null;
    const q = query.trim().toLowerCase();
    return apps.filter((a) => {
      if (filter !== "all" && a.status !== filter) return false;
      if (!q) return true;
      return (
        a.venue_name.toLowerCase().includes(q) ||
        a.contact_email.toLowerCase().includes(q) ||
        a.city.toLowerCase().includes(q) ||
        a.contact_name.toLowerCase().includes(q)
      );
    });
  }, [apps, filter, query]);

  async function callFunction(
    fn: "approve-venue" | "reject-venue" | "send-venue-email",
    body: Record<string, unknown>
  ): Promise<Record<string, unknown> | null> {
    // Force a session refresh so we always hand the Edge Function a
    // fresh access token. Without this, a dashboard that's been open
    // past the 1h access-token TTL will send a stale JWT and Supabase
    // rejects with 401 before the function even runs.
    await supabase.auth.refreshSession();
    const { data: session } = await supabase.auth.getSession();
    const jwt = session?.session?.access_token;
    if (!jwt) {
      toast.error("You're signed out - please sign in again.");
      return null;
    }
    try {
      const res = await fetch(`${env.supabaseUrl}/functions/v1/${fn}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwt}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error((json?.error as string) || `Request failed (${res.status})`);
        return null;
      }
      return json as Record<string, unknown>;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Network error");
      return null;
    }
  }

  // Approve / reject fire directly. We considered a reauth gate but
  // Supabase's auth.reauthenticate() is built for password-change
  // flows (generates a nonce for updateUser), not a generic "prove
  // it's you" gate - and these actions are audit-logged + reversible,
  // so gating them adds friction for little security gain. Reintroduce
  // reauth for genuinely destructive actions later (permanent delete,
  // ban-with-no-appeal, etc.) using a known-working primitive.
  async function approve(app: Application) {
    if (actingId) return;
    setActingId(app.id);
    const result = await callFunction("approve-venue", {
      application_id: app.id,
    });
    setActingId(null);
    if (result) {
      if (result.was_new_user === false) {
        toast.success(
          `Approved · ${app.contact_email} already had an account. Ask them to sign in at whistll.com/login.`,
          { duration: 8000 },
        );
      } else {
        toast.success(`Approved · invite sent to ${app.contact_email}`);
      }
      await load();
    }
  }

  /**
   * Fires the "venue_followup_external_system" email - for venues with
   * an external booking system who got the Tier-2 Whistll-dashboard
   * version of the welcome email and need the corrected framing
   * (steps 3+4 collapse into a one-time connect-system setup). Tier 2
   * venues with booking_system='__none__' don't need this - the button
   * is hidden for them in the JSX below.
   */
  async function sendFollowup(app: Application) {
    if (actingId) return;
    if (
      !window.confirm(
        `Send the ${app.booking_system} follow-up email to ${app.contact_email}?\n\nThis is the corrected version that reframes steps 3 + 4 around connecting their booking system, not adding courts manually.`,
      )
    ) {
      return;
    }
    setActingId(app.id);
    const result = await callFunction("send-venue-email", {
      kind: "venue_followup_external_system",
      venue_application_id: app.id,
      launch_date: "2026-06-01",
    });
    setActingId(null);
    if (result) {
      toast.success(`Follow-up sent to ${app.contact_email}`);
    }
  }

  async function reject(app: Application) {
    if (actingId) return;
    setActingId(app.id);
    const result = await callFunction("reject-venue", {
      application_id: app.id,
      reason: rejectReason.trim() || null,
    });
    setActingId(null);
    if (result) {
      toast.success("Rejected");
      setRejectingId(null);
      setRejectReason("");
      await load();
    }
  }

  return (
    <div>
      <header className="mb-6">
        <p className="mb-1.5 text-xs font-bold uppercase tracking-wider text-volt">
          Admin · Venues
        </p>
        <h1 className="text-3xl font-semibold tracking-tight">
          Venue applications
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Approving a venue emails them a sign-in link and unlocks their
          dashboard. Rejections are final but the contact gets no auto-
          email - follow up manually if needed.
        </p>
      </header>

      {/* Filter row */}
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by venue, email, city, contact…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <Filter className="h-3.5 w-3.5 text-muted-foreground" />
          {(["pending", "approved", "rejected", "all"] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={
                "rounded-full px-3 py-1 text-xs font-semibold capitalize transition-colors " +
                (filter === f
                  ? "bg-volt text-primary-foreground"
                  : "bg-secondary text-muted-foreground hover:text-foreground")
              }
            >
              {f === "all" ? "All" : f}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {filtered === null ? (
        <PageLoader label="Loading applications…" />
      ) : filtered.length === 0 ? (
        <EmptyState filter={filter} />
      ) : (
        <ul className="flex flex-col gap-2">
          {filtered.map((app) => {
            const expanded = expandedId === app.id;
            const rejecting = rejectingId === app.id;
            const working = actingId === app.id;
            const meta = STATUS_META[app.status];

            return (
              <li
                key={app.id}
                className="overflow-hidden rounded-2xl border border-border bg-card"
              >
                <button
                  type="button"
                  onClick={() => {
                    setExpandedId(expanded ? null : app.id);
                    if (rejectingId !== app.id) setRejectingId(null);
                  }}
                  className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-secondary/40"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="truncate font-semibold">
                        {app.venue_name}
                      </h3>
                      <span
                        className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold"
                        style={{ background: meta.bg, color: meta.color }}
                      >
                        {meta.icon}
                        {meta.label}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {app.city}, {app.country}
                      </span>
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {app.contact_email}
                      </span>
                      <span className="hidden sm:block">
                        {new Date(app.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  <ChevronDown
                    className={cn(
                      "mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                      expanded && "rotate-180 text-volt"
                    )}
                  />
                </button>

                {expanded && (
                  <div className="border-t border-border px-4 py-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <Detail label="Contact">
                        <p className="text-sm">{app.contact_name}</p>
                        <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Mail className="h-3 w-3" /> {app.contact_email}
                        </p>
                        <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Phone className="h-3 w-3" /> {app.contact_phone}
                        </p>
                      </Detail>
                      <Detail label="Address">
                        <p className="text-sm">{app.address_line1}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {app.city}, {app.region}
                        </p>
                      </Detail>
                      <Detail label="Booking system">
                        <p className="text-sm">
                          {app.booking_system === "__none__"
                            ? "None (will use Whistll dashboard)"
                            : app.booking_system || "Not specified"}
                        </p>
                      </Detail>
                      <Detail label="Status history">
                        <p className="text-xs text-muted-foreground">
                          Submitted{" "}
                          {new Date(app.created_at).toLocaleString()}
                        </p>
                        {app.reviewed_at && (
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            Reviewed{" "}
                            {new Date(app.reviewed_at).toLocaleString()}
                          </p>
                        )}
                        {app.invited_at && (
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            Invited{" "}
                            {new Date(app.invited_at).toLocaleString()}
                          </p>
                        )}
                        {app.rejection_reason && (
                          <p className="mt-0.5 text-xs text-[#f87171]">
                            Reason: {app.rejection_reason}
                          </p>
                        )}
                      </Detail>
                    </div>

                    {app.notes && (
                      <Detail label="Notes from applicant" className="mt-4">
                        <p className="text-sm">{app.notes}</p>
                      </Detail>
                    )}

                    {/* Onboarding + performance - only meaningful for
                        approved venues. Lazy-loaded on expand. */}
                    {app.status === "approved" && (
                      <VenueOpsPanel
                        app={app}
                        snapshot={snapshots[app.id]}
                      />
                    )}

                    {/* Action row - only while pending */}
                    {app.status === "pending" && !rejecting && (
                      <div className="mt-5 flex flex-col gap-2 sm:flex-row">
                        <Button
                          size="lg"
                          onClick={() => approve(app)}
                          disabled={working}
                        >
                          {working ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Approving…
                            </>
                          ) : (
                            <>
                              <CheckCircle2 className="h-4 w-4" />
                              Approve & invite
                            </>
                          )}
                        </Button>
                        <Button
                          size="lg"
                          variant="outline"
                          onClick={() => {
                            setRejectingId(app.id);
                            setRejectReason("");
                          }}
                          disabled={working}
                        >
                          <XCircle className="h-4 w-4" />
                          Reject
                        </Button>
                      </div>
                    )}

                    {/* Post-approval action - only relevant for venues with
                        an external booking system (Tier 1). Lets us re-fire
                        the corrected welcome email if the original landed
                        with the wrong framing (which happens for any venue
                        approved before the venue_approved template became
                        booking-system-aware). Tier-2 venues running on the
                        Whistll dashboard hide this. */}
                    {app.status === "approved" &&
                      app.booking_system &&
                      app.booking_system !== "__none__" && (
                        <div className="mt-5">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => sendFollowup(app)}
                            disabled={working}
                          >
                            {working ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Sending…
                              </>
                            ) : (
                              <>
                                <Mail className="h-4 w-4" />
                                Send {app.booking_system} follow-up
                              </>
                            )}
                          </Button>
                          <p className="mt-2 text-xs text-muted-foreground">
                            Sends the corrected welcome that reframes the
                            courts/availability steps around their{" "}
                            {app.booking_system} integration. Use once if
                            they got the original Whistll-dashboard email
                            by mistake.
                          </p>
                        </div>
                      )}

                    {/* Reject reason form */}
                    {rejecting && (
                      <div className="mt-5 rounded-lg border border-destructive/40 bg-destructive/5 p-3">
                        <label className="mb-2 block text-xs font-semibold">
                          Reason (shown to venue if you follow up)
                        </label>
                        <Textarea
                          rows={2}
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          placeholder="e.g. Not enough courts for initial launch cities."
                        />
                        <div className="mt-3 flex gap-2">
                          <Button
                            variant="destructive"
                            onClick={() => reject(app)}
                            disabled={working}
                          >
                            {working ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Rejecting…
                              </>
                            ) : (
                              "Confirm reject"
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              setRejectingId(null);
                              setRejectReason("");
                            }}
                            disabled={working}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

    </div>
  );
}

function EmptyState({ filter }: { filter: Status | "all" }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card/40 py-16 text-center">
      <p className="text-sm font-semibold">No {filter === "all" ? "" : filter} applications.</p>
      <p className="mt-1 text-xs text-muted-foreground">
        New applications show up here the second they're submitted.
      </p>
    </div>
  );
}

function Detail({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      {children}
    </div>
  );
}

function cn(...parts: Array<string | false | undefined>) {
  return parts.filter(Boolean).join(" ");
}

// ----------------------------------------------------------------------------
// Venue ops panel - what the admin sees inside the expanded row of an
// approved venue. Mirrors the venue's own dashboard so we can track
// progress + performance from the same screen we approve from.
// ----------------------------------------------------------------------------

function VenueOpsPanel({
  app,
  snapshot,
}: {
  app: Application;
  snapshot: VenueSnapshot | null | undefined;
}) {
  const stripeDone = app.stripe_connect_status === "enabled";
  const stripeRestricted = app.stripe_connect_status === "restricted";
  const courtsDone = (snapshot?.courtCount ?? 0) > 0;
  const slotsDone = (snapshot?.slotCount ?? 0) > 0;
  const agreementDone = !!app.vp_agreement_signed_at;
  const completedSteps =
    1 /* application approved */ +
    (stripeDone ? 1 : 0) +
    (courtsDone ? 1 : 0) +
    (slotsDone ? 1 : 0) +
    (agreementDone ? 1 : 0);

  return (
    <div className="mt-6 space-y-5 border-t border-border pt-5">
      {/* Onboarding progress */}
      <section>
        <div className="mb-3 flex items-center justify-between">
          <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Onboarding progress
          </h4>
          <span
            className={cn(
              "rounded-full px-2 py-0.5 text-[10px] font-bold",
              completedSteps === 5
                ? "bg-volt text-primary-foreground"
                : "bg-secondary text-muted-foreground",
            )}
          >
            {completedSteps} / 5
          </span>
        </div>
        <ul className="grid gap-2 sm:grid-cols-2">
          <OnboardingStep
            done={true}
            icon={<CheckCircle2 className="h-3.5 w-3.5" />}
            label="Application approved"
            sub={
              app.reviewed_at
                ? `Approved ${new Date(app.reviewed_at).toLocaleDateString()}`
                : undefined
            }
          />
          <OnboardingStep
            done={stripeDone}
            warning={stripeRestricted}
            icon={<Wallet className="h-3.5 w-3.5" />}
            label="Stripe connected"
            sub={
              stripeDone
                ? "Connect status: enabled"
                : stripeRestricted
                  ? "Connect status: restricted (action needed)"
                  : app.stripe_connect_status
                    ? `Status: ${app.stripe_connect_status}`
                    : "Not started"
            }
          />
          <OnboardingStep
            done={courtsDone}
            icon={<Building2 className="h-3.5 w-3.5" />}
            label="Courts listed"
            sub={
              snapshot === undefined
                ? "-"
                : snapshot === null
                  ? "Loading…"
                  : `${snapshot.courtCount} court${snapshot.courtCount === 1 ? "" : "s"}`
            }
          />
          <OnboardingStep
            done={slotsDone}
            icon={<CalendarRange className="h-3.5 w-3.5" />}
            label="Availability set"
            sub={
              snapshot === undefined
                ? "-"
                : snapshot === null
                  ? "Loading…"
                  : `${snapshot.slotCount} upcoming slot${snapshot.slotCount === 1 ? "" : "s"}`
            }
          />
          <OnboardingStep
            done={agreementDone}
            icon={<FileText className="h-3.5 w-3.5" />}
            label="Partner agreement"
            sub={
              app.vp_agreement_signed_at
                ? `Signed ${new Date(app.vp_agreement_signed_at).toLocaleDateString()}`
                : "Not signed"
            }
          />
        </ul>
      </section>

      {/* Performance snapshot */}
      <section>
        <div className="mb-3 flex items-center justify-between">
          <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Performance
          </h4>
        </div>
        {snapshot === undefined ? (
          <p className="text-xs text-muted-foreground">
            Expand the row to load.
          </p>
        ) : snapshot === null ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Crunching their numbers…
          </div>
        ) : snapshot.allTimeConfirmed === 0 ? (
          <p className="rounded-md border border-dashed border-border bg-card/40 p-3 text-xs text-muted-foreground">
            No confirmed runs yet - performance numbers light up the
            moment their first match hits its player threshold.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <PerfMini
              icon={<Swords className="h-3.5 w-3.5" />}
              label="Bookings · this month"
              value={snapshot.monthlyConfirmed}
              sub={`${snapshot.allTimeConfirmed} all-time`}
            />
            <PerfMini
              icon={<Wallet className="h-3.5 w-3.5" />}
              label="Court revenue · this month"
              value={formatMoneyMinor(
                snapshot.monthlyRevenueMinor,
                snapshot.currency,
              )}
              sub={`${formatMoneyMinor(snapshot.allTimeRevenueMinor, snapshot.currency)} all-time`}
              tone="volt"
            />
            <PerfMini
              icon={<TrendingUp className="h-3.5 w-3.5" />}
              label="Avg fill rate"
              value={
                snapshot.totalTargetSeats > 0
                  ? `${Math.round((snapshot.capturedSeats / snapshot.totalTargetSeats) * 100)}%`
                  : "-"
              }
              sub={`${snapshot.capturedSeats} captured seats`}
            />
            <PerfMini
              icon={<Flag className="h-3.5 w-3.5" />}
              label="No-show rate"
              value={
                snapshot.capturedSeats > 0
                  ? `${Math.round((snapshot.flagCount / snapshot.capturedSeats) * 100 * 10) / 10}%`
                  : "-"
              }
              sub={`${snapshot.flagCount} flag${snapshot.flagCount === 1 ? "" : "s"}`}
            />
          </div>
        )}
      </section>
    </div>
  );
}

function OnboardingStep({
  done,
  warning,
  icon,
  label,
  sub,
}: {
  done: boolean;
  warning?: boolean;
  icon: React.ReactNode;
  label: string;
  sub?: string;
}) {
  const dot = warning ? (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#f87171]/20 text-[#f87171]">
      <AlertTriangle className="h-3 w-3" />
    </span>
  ) : done ? (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-volt text-primary-foreground">
      <CheckCircle2 className="h-3 w-3" />
    </span>
  ) : (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-border bg-background text-muted-foreground">
      <span className="h-1 w-1 rounded-full bg-muted-foreground" />
    </span>
  );
  return (
    <li className="flex items-start gap-2 rounded-md border border-border bg-card p-2.5">
      {dot}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">{icon}</span>
          <p className="text-xs font-semibold">{label}</p>
        </div>
        {sub && (
          <p className="mt-0.5 text-[11px] text-muted-foreground">{sub}</p>
        )}
      </div>
    </li>
  );
}

function PerfMini({
  icon,
  label,
  value,
  sub,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  sub?: string;
  tone?: "volt";
}) {
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        <span className={tone === "volt" ? "text-volt" : undefined}>
          {icon}
        </span>
        {label}
      </div>
      <p
        className={cn(
          "text-base font-black tracking-tight",
          tone === "volt" && "text-volt",
        )}
      >
        {value}
      </p>
      {sub && (
        <p className="mt-0.5 text-[10px] leading-snug text-muted-foreground">
          {sub}
        </p>
      )}
    </div>
  );
}
